import { computed } from 'mobx';
import { observer } from 'mobx-react';
import {
    autoinject,
    Duration,
    Enums,
    formatDuration,
    inject,
    Tooltip,
} from 'Mri.Web.Common';
import * as React from 'react';
import { AccountStore } from '../../../Account/AccountStore';
import { IDisplayFieldProps } from './DisplayFields';

interface IMeasurementFieldProps extends IDisplayFieldProps {
    readonly id: Mri.WebApi.MeasurementId;
    readonly duration?: boolean;
    readonly fixedCount?: number;
    readonly fallbackValue?: any;
    readonly ignoreZero?: boolean;
}

@autoinject
@observer
export class MeasurementField extends React.Component<IMeasurementFieldProps> {
    public render() {
        const { fixedCount, fallbackValue, ignoreZero, data } = this.props;
        let decimals = this.accountStore.storage.gridDecimals || fixedCount;
        if (this.measurement && ignoreZero) {
            if (
                this.measurement.doubleValue == 0 ||
                this.measurement.intValue == 0 ||
                this.measurement.floatValue == 0
            ) {
                return (
                    <div>
                        {fallbackValue ?? '-'}
                    </div>
                );
            }
        }

        if (!this.measurement)
            return (
                <div>
                    {fallbackValue ?? '-'}
                </div>
            );

        if (this.measurement) {
            const units = !this.accountStore.storage.hideUnits ? this.measurement.units : '';
            if (this.measurement.boolValue != null) {
                return (
                    <div>
                        {this.measurement.boolValue ? 'true' : 'false'}
                    </div>
                );
            } else if (this.measurement.doubleValue != null) {
                let value: string | number = this.measurement.doubleValue;
                if (this.measurement.units == '%') {
                    if (value >= 0 && value <= 1) {
                        value = Math.trunc(value * 100);
                    } 
                    if(!this.accountStore.storage.hideUnits) {
                        value = value + '%'
                    }
                } else if (units) {
                    value = decimals ? value.toFixed(decimals) : value;
                    value = value + ' ' + units;
                } else {
                    value = decimals ? value.toFixed(decimals) : value;
                }
                return (
                    <div>
                        {value ?? fallbackValue}
                    </div>
                );
            } else if (this.measurement.floatValue != null) {
                let value: string | number = this.measurement.floatValue;
                if (this.measurement.units == '%') {
                    if (value >= 0 && value <= 1) {
                        value = Math.trunc(value * 100);
                    }
                    if(!this.accountStore.storage.hideUnits) {
                        value = value + '%'
                    }
                }else if (this.measurement.units == 'C') {
                    value = decimals ? value.toFixed(decimals) : Math.round(value);
                    value = value + ' ' + units;
                } else if (units) {
                    value = decimals ? value.toFixed(decimals) : value;
                    value = value + ' ' + units;
                } else {
                    value = decimals ? value.toFixed(decimals) : value;
                }
                return (
                    <div>
                        {value ?? fallbackValue}
                    </div>
                );
            } else if (this.measurement.intValue != null) {
                let value: string | number = this.measurement.intValue;
                if (this.props.duration) {
                    return (
                        <div>
                            {formatDuration(new Duration(value))}
                        </div>
                    );
                }
                if (units == '%' && value >= 0 && value <= 100) {
                    value = value + '%';
                } else if (units) {
                    value = value + ' ' + units;
                }
                return (
                    <div>
                        {value}
                    </div>
                );
            } else if (this.measurement.stringValue != null) {
                return (
                    <div>
                        {this.measurement.stringValue}
                    </div>
                );
            } else if (this.measurement.durationValue != null) {
                return (
                    <div>
                        {formatDuration(this.measurement.durationValue)}
                    </div>
                );
            } else {
                return (
                    <div>
                        -
                    </div>
                );
            }
        }
    }

    @computed
    private get measurement() {
        const { data, id } = this.props;
        const display = data;

        if (!display || !display.measurements) return;

        if (display) return display.measurements.find(s => s.id == id);
    }

    @inject
    private accountStore!: AccountStore;
}

@observer
export class MeasurementSourceField extends React.Component<IMeasurementFieldProps> {
    public render() {
        if (!this.measurement || !this.measurement.intValue)
            return (
                <div>
                    -
                </div>
            );

        /* eslint-disable no-bitwise */
        const source = this.measurement.intValue & Mri.WebApi.MeasurementId.SENSOR_ID_SOURCE_MASK;
        const id = this.measurement.intValue & Mri.WebApi.MeasurementId.SENSOR_ID_MASK;
        /* eslint-enable no-bitwise */
        let prefix = Enums.MeasurementIdSourcePrefix.get(source) || '';
        prefix = prefix ? ` (${prefix})` : '';
        const title = Enums.BriefMeasurementId.has(id)
            ? Enums.toString(Enums.BriefMeasurementId, id)
            : Enums.toString(Enums.MeasurementId, id);
        let tooltip = Enums.toString(Enums.MeasurementId, id);
        if (source) tooltip = Enums.toString(Enums.MeasurementId, source) + ' ' + tooltip;

        return (
            <div>
                <Tooltip message={tooltip}>{title + prefix}</Tooltip>
            </div>
        );
    }

    @computed
    private get measurement() {
        const { data, id } = this.props;
        const display = data;

        if (!display || !display.measurements) return;

        if (display) return display.measurements.find(s => s.id == id);
    }
}

interface IMeasurementSourceSpanFieldProps extends IDisplayFieldProps {
    readonly id: Mri.WebApi.MeasurementId;
    readonly prefixComma?: boolean;
}
@observer
export class MeasurementSourceSpanField extends React.Component<IMeasurementSourceSpanFieldProps> {
    public render() {
        if (!this.measurement || !this.measurement.intValue) return '';

        /* eslint-disable no-bitwise */
        const source = this.measurement.intValue & Mri.WebApi.MeasurementId.SENSOR_ID_SOURCE_MASK;
        const id = this.measurement.intValue & Mri.WebApi.MeasurementId.SENSOR_ID_MASK;
        /* eslint-enable no-bitwise */
        let prefix = Enums.MeasurementIdSourcePrefix.get(source) || '';
        prefix = prefix ? ` (${prefix})` : '';
        const title = Enums.BriefMeasurementId.has(id)
            ? Enums.toString(Enums.BriefMeasurementId, id)
            : Enums.toString(Enums.MeasurementId, id);
        let tooltip = Enums.toString(Enums.MeasurementId, id);
        if (source) tooltip = Enums.toString(Enums.MeasurementId, source) + ' ' + tooltip;

        return <span>{this.props.prefixComma ? ', ' + title + prefix : title + prefix}</span>;
    }

    @computed
    private get measurement() {
        const { data, id } = this.props;
        const display = data;

        if (!display || !display.measurements) return;

        if (display) return display.measurements.find(s => s.id == id);
    }
}

interface IVideoInputOutputFieldProps extends IDisplayFieldProps {
    readonly id: Mri.WebApi.MeasurementId;
}

@observer
export class VideoInputOutputField extends React.Component<IVideoInputOutputFieldProps> {
    public render() {
        if (!this.measurement)
            return (
                <div>
                    -
                </div>
            );

        if (this.measurement.intValue != null) {
            const value: string | number = this.measurement.intValue;

            return (
                <div>
                    {Enums.toString(Enums.VideoInputOutputState, value)}
                </div>
            );
        } else {
            return (
                <div>
                    -
                </div>
            );
        }
    }

    @computed
    private get measurement() {
        const { data, id } = this.props;
        const display = data;

        if (!display || !display.measurements) return;

        if (display) return display.measurements.find(s => s.id == id);
    }
}

interface IVideoRXTXFieldProps extends IDisplayFieldProps {
    readonly id: Mri.WebApi.MeasurementId;
}

@observer
export class VideoRXTXField extends React.Component<IVideoRXTXFieldProps> {
    public render() {
        if (!this.measurement)
            return (
                <div>
                    -
                </div>
            );

        if (this.measurement.intValue != null) {
            const value: string | number = this.measurement.intValue;

            return (
                <div>
                    {Enums.toString(Enums.RXTXState, value)}
                </div>
            );
        } else {
            return (
                <div>
                    -
                </div>
            );
        }
    }

    @computed
    private get measurement() {
        const { data, id } = this.props;
        const display = data;

        if (!display || !display.measurements) return;

        if (display) return display.measurements.find(s => s.id == id);
    }
}

interface IPowerGovernorSourceFieldProps extends IDisplayFieldProps {
    readonly id: Mri.WebApi.MeasurementId;
}

@observer
export class PowerGovernorSourceField extends React.Component<IPowerGovernorSourceFieldProps> {
    public render() {
        if (!this.measurement)
            return (
                <div>
                    -
                </div>
            );

        if (this.measurement.intValue != null) {
            const value: string | number = this.measurement.intValue;
            return (
                <div>
                    {Enums.toString(Enums.PowerGovernorSourceState, value) || '-'}
                </div>
            );
        } else {
            return (
                <div>
                    -
                </div>
            );
        }
    }

    @computed
    private get measurement() {
        const { data, id } = this.props;
        const display = data;

        if (!display || !display.measurements) return;

        if (display) return display.measurements.find(s => s.id == id);
    }
}

interface IChartSourceFieldProps extends IDisplayFieldProps {
    readonly id: Mri.WebApi.MeasurementId;
}

@observer
export class ChartSourceField extends React.Component<IChartSourceFieldProps> {
    public render() {
        if (!this.measurement)
            return (
                <div>
                    -
                </div>
            );

        if (this.measurement.intValue != null) {
            const value: string | number = this.measurement.intValue;
            return (
                <div>
                    {Enums.toString(Enums.CoolVuZone1ChartSource, value) || '-'}
                </div>
            );
        } else {
            return (
                <div>
                    -
                </div>
            );
        }
    }

    @computed
    private get measurement(): Mri.WebApi.Measurement | undefined {
        const { data, id } = this.props;
        const display = data;

        if (!display || !display.measurements) return;

        if (display) return display.measurements.find(s => s.id == id);
    }
}

interface IBooleanMeasurementFieldProps extends IDisplayFieldProps {
    readonly id: Mri.WebApi.MeasurementId;
    readonly trueValue: string;
    readonly falseValue: string;
}

@observer
export class BooleanMeasurementField extends React.Component<IBooleanMeasurementFieldProps> {
    public render() {
        if (this.measurement?.boolValue == null)
            return (
                <div>
                    -
                </div>
            );

        return (
            <div>
                {this.measurement.boolValue ? this.props.trueValue : this.props.falseValue}
            </div>
        );
    }

    @computed
    private get measurement() {
        const { data, id } = this.props;
        const display = data;

        if (!display || !display.measurements) return;

        if (display) return display.measurements.find(s => s.id == id);
    }
}

interface IWeatherCodeMeasurementFieldProps extends IDisplayFieldProps {
    readonly id: Mri.WebApi.MeasurementId;
}

@observer
export class WeatherCodeMeasurementField extends React.Component<IWeatherCodeMeasurementFieldProps> {
    public render() {
        if (this.measurement?.intValue == null)
            return (
                <div>
                    -
                </div>
            );
        const description = Enums.toString(Enums.WeatherCodeDescription, this.measurement.intValue);
        return (
            <div>
                {description}
            </div>
        );
    }

    @computed
    private get measurement() {
        const { data, id } = this.props;
        const display = data;

        if (!display || !display.measurements) return;

        if (display) return display.measurements.find(s => s.id == id);
    }
}

export function getMeasurementValue(measurements: Mri.WebApi.Measurement[], id: Mri.WebApi.MeasurementId, fixedCount?: number, duration?: boolean){
    let measurement= measurements.find(m => m.id == id);
    if (!measurement) return null;
    
    const units = measurement.units;
    if (measurement.boolValue != null) {
        return measurement.boolValue ? 'true' : 'false';
    } else if (measurement.doubleValue != null) {
        let value: string | number = measurement.doubleValue;
        if (units == '%') {
            if (value >= 0 && value <= 1) {
                value = Math.trunc(value * 100) + '%';
            } else if (value >= 0 && value <= 100) {
                value = value + '%';
            }
        } else if (units) {
            value = fixedCount ? value.toFixed(fixedCount) : value;
            value = value + ' ' + units;
        } else {
            value = fixedCount ? value.toFixed(fixedCount) : value;
        }
        return value;
    } else if (measurement.floatValue != null) {
        let value: string | number = measurement.floatValue;
        if (units == '%') {
            if (value >= 0 && value <= 1) {
                value = Math.trunc(value * 100) + '%';
            } else if (value >= 0 && value <= 100) {
                value = value + '%';
            }
        } else if (units) {
            value = fixedCount ? value.toFixed(fixedCount) : value;
            value = value + ' ' + units;
        } else {
            value = fixedCount ? value.toFixed(fixedCount) : value;
        }
        return  value;
    } else if (measurement.intValue != null) {
        let value: string | number = measurement.intValue;
        if (duration) {
            return formatDuration(new Duration(value));
        }
        if (units == '%' && value >= 0 && value <= 100) {
            value = value + '%';
        } else if (units) {
            value = value + ' ' + units;
        }
        return value;
    } else if (measurement.stringValue != null) {
        return measurement.stringValue;
    } else if (measurement.durationValue != null) {
        return formatDuration(measurement.durationValue);
    } else {
        return null;
    }
}

export function getBooleanMeasurementValue(measurements: Mri.WebApi.Measurement[], id: Mri.WebApi.MeasurementId, trueValue: string, falseValue: string) {
    let measurement= measurements.find(m => m.id == id);
    if (!measurement) return null;

    if (measurement.boolValue != null) {
        return measurement.boolValue ? trueValue : falseValue;
    } else {
        return null;
    }
}

export function getWeatherCodeMeasurementValue(measurements: Mri.WebApi.Measurement[], id: Mri.WebApi.MeasurementId) {
    let measurement= measurements.find(m => m.id == id);
    if (!measurement) return null;

    if (measurement.intValue != null) {
        return Enums.toString(Enums.WeatherCodeDescription, measurement.intValue);
    } else {
        return null;
    }
}

export function getChartSourceMeasurementValue(measurements: Mri.WebApi.Measurement[], id: Mri.WebApi.MeasurementId) {
    let measurement= measurements.find(m => m.id == id);
    if (!measurement) return null;

    if (measurement.intValue != null) {
        return Enums.toString(Enums.CoolVuZone1ChartSource, measurement.intValue);
    } else {
        return null;
    }
}

export function getPowerGovernorSourceMeasurementValue(measurements: Mri.WebApi.Measurement[], id: Mri.WebApi.MeasurementId) {
    let measurement= measurements.find(m => m.id == id);
    if (!measurement) return null;

    if (measurement.intValue != null) {
        return Enums.toString(Enums.PowerGovernorSourceState, measurement.intValue);
    } else {
        return null;
    }
}

export function getVideoRXTXMeasurementValue(measurements: Mri.WebApi.Measurement[], id: Mri.WebApi.MeasurementId) {
    let measurement= measurements.find(m => m.id == id);
    if (!measurement) return null;

    if (measurement.intValue != null) {
        return Enums.toString(Enums.RXTXState, measurement.intValue);
    } else {
        return null;
    }
}

export function getVideoInputOutputMeasurementValue(measurements: Mri.WebApi.Measurement[], id: Mri.WebApi.MeasurementId) {
    let measurement= measurements.find(m => m.id == id);
    if (!measurement) return null;

    if (measurement.intValue != null) {
        return Enums.toString(Enums.VideoInputOutputState, measurement.intValue);
    } else {
        return null;
    }
}